import { Loader2, LoaderCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { ProductImageFallback } from '@/components/ProductImageFallback';
import { SearchProductsForm } from '@/components/SearchProductsForm';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { ROUTE_PATHS } from '@/config/constants';
import { withSearchProductsFilters } from '@/hocs/withSearchProductsFilter';
import { useForceUpdate } from '@/hooks/useForceUpdate';
import { useGetProducts } from '@/hooks/useGetProducts';
import {
  Product,
  UpdatePairsWithAction,
  UpdatePairsWithDto,
} from '@/services/ProductsService';

interface ProductItemProps {
  p: Product;
  isButtonDisabled: boolean;
  isSameProduct: boolean;
  isAlreadyPaired: boolean;
  isLoading: boolean;
  onAdd: (pairsWithId: string) => void;
}

function ProductItem({
  p,
  isAlreadyPaired,
  isButtonDisabled,
  isLoading,
  isSameProduct,
  onAdd,
}: ProductItemProps) {
  const [imageError, setImageError] = useState(false);

  const { t } = useTranslation('translation', {
    keyPrefix:
      'pages.VisionProduct.components.PairsWith.components.AddProductModal',
  });

  return (
    <li>
      <Card className="transition-shadow hover:shadow-md">
        <CardContent className="p-4">
          <div className="flex flex-col items-center">
            <Link
              to={ROUTE_PATHS.VISION_PRODUCT.replace(
                ':productId',
                String(p.id),
              )}
              target="_blank"
              className="mb-4 aspect-square h-40 w-40 overflow-hidden rounded-lg bg-gray-100"
            >
              {imageError ? (
                <ProductImageFallback />
              ) : (
                <img
                  src={p.image1}
                  alt={p.name}
                  onError={() => setImageError(true)}
                />
              )}
            </Link>

            <div className="space-y-2 text-center">
              <h4 className="font-medium">{p.name}</h4>

              <Button
                disabled={isButtonDisabled}
                onClick={() => onAdd(p.productId)}
                className="mt-4 w-full disabled:cursor-not-allowed"
              >
                {isSameProduct && t('buttonLabelVariants.sameProduct')}

                {isAlreadyPaired && t('buttonLabelVariants.paired')}

                {isLoading && <Loader2 className="animate-spin" />}

                {!isButtonDisabled && t('buttonLabelVariants.add')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </li>
  );
}

interface AddProductModalProps {
  open: boolean;
  productId: string;
  pairsWith: string[];
  currentlyLoadingPairsWithId: null | string;
  onUpdatePairsWith: (updatePairsWithDto: UpdatePairsWithDto) => Promise<void>;
  onClose: () => void;
}

export const AddProductModal = withSearchProductsFilters<AddProductModalProps>(
  ({
    currentlyLoadingPairsWithId,
    handleSearchFilters,
    onClose,
    onUpdatePairsWith,
    open,
    pairsWith,
    productId,
    searchFilters,
  }) => {
    const endProductsRef = useRef<HTMLDivElement>(null);

    const { count, forceUpdate } = useForceUpdate();

    const { t } = useTranslation('translation', {
      keyPrefix:
        'pages.VisionProduct.components.PairsWith.components.AddProductModal',
    });

    async function handleAdd(pairsWithId: string) {
      await onUpdatePairsWith({
        action: UpdatePairsWithAction.ADD,
        productId,
        pairsWithId,
      });
    }

    const {
      products,
      isFetching,
      isFetchingNextPage,
      hasNextPage,
      fetchNextPage,
    } = useGetProducts({ ...searchFilters, unique: true });

    useEffect(() => {
      if (open) {
        forceUpdate();
      }
    }, [forceUpdate, open]);

    useEffect(() => {
      if (!endProductsRef.current) {
        return;
      }

      const observer = new IntersectionObserver(
        (entries, obs) => {
          const { isIntersecting } = entries[0];

          if (!hasNextPage) {
            obs.disconnect();
            return;
          }

          if (isIntersecting && !isFetchingNextPage) {
            fetchNextPage();
          }
        },
        {
          rootMargin: '30px',
        },
      );

      observer.observe(endProductsRef.current);

      return () => {
        observer.disconnect();
      };
    }, [count, isFetchingNextPage, hasNextPage, fetchNextPage]);

    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('title')}</DialogTitle>
          </DialogHeader>

          <DialogDescription />

          <div className="space-y-6">
            <SearchProductsForm
              searchFilters={searchFilters}
              onSearchFilters={handleSearchFilters}
            />

            <div className="max-h-[400px] overflow-scroll">
              {products && products.length > 0 && (
                <ul className="grid grid-cols-2 gap-2">
                  {products.map((p) => {
                    const isLoading =
                      currentlyLoadingPairsWithId === p.productId;

                    const isSameProduct =
                      !isLoading && productId === p.productId;

                    const isAlreadyPaired =
                      !isLoading && pairsWith.includes(p.productId);

                    const isButtonDisabled =
                      isLoading || isSameProduct || isAlreadyPaired;

                    return (
                      <ProductItem
                        key={p.id}
                        p={p}
                        isAlreadyPaired={isAlreadyPaired}
                        isButtonDisabled={isButtonDisabled}
                        isSameProduct={isSameProduct}
                        isLoading={isLoading}
                        onAdd={handleAdd}
                      />
                    );
                  })}
                </ul>
              )}

              <div
                ref={endProductsRef}
                className="mx-auto mt-10 flex items-center justify-center"
              >
                {isFetching && <LoaderCircle className="animate-spin" />}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  },
);
